import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';

import type { Grabber } from '../input/Grabbers';
import type { RoomState } from '../room/RoomState';
import type { RoomView } from '../room/RoomView';

/**
 * The near-reach test — which node, if any, a grabber is close enough to grab.
 *
 * Briefly (`3fb3a60` through Link mode landing) this was also read by `ConnectionDrawer`, to
 * *refuse* to start a connection on a node within reach: once the dominant trigger started
 * doing both grabbing and connecting within one mode (build), this was the proximity tie-break
 * that decided which a press meant, since the two gestures could no longer disagree about which
 * node counted as "in reach" if they shared one test. That was an interim stopgap, not a
 * permanent second caller — plan/mode-hierarchy.md's own "Sequencing" section calls it out as
 * shipped ahead of Link mode existing, on purpose, so the trigger-grab change could be tested in
 * isolation first. Grabbing (Build's trigger) and linking (Link's trigger) are now structurally
 * confined to disjoint modes, so `ConnectionDrawer` no longer needs a proximity opinion at all —
 * `PossessionManager` is this function's sole caller again.
 *
 * See `PossessionManager.ts`'s own doc for why GRAB_REACH is the value it is.
 */
const GRAB_REACH = 0.08;

/**
 * Whether `point` is close enough to `mesh` to count as a grab — within the mesh's own world
 * bounding-sphere radius, plus `GRAB_REACH`.
 *
 * `computeWorldMatrix(true)` is forced before reading the bounding sphere: both callers run
 * from `onBeforeRenderObservable`, and an unforced read there can return the *previous*
 * frame's world matrix regardless of registration order — see CLAUDE.md, "An unforced
 * `computeWorldMatrix` can return last frame's answer".
 */
export function isWithinGrabReach(point: Vector3, mesh: AbstractMesh): boolean {
  mesh.computeWorldMatrix(true);
  const bounding = mesh.getBoundingInfo().boundingSphere;
  return Vector3.Distance(point, bounding.centerWorld) <= bounding.radiusWorld + GRAB_REACH;
}

/**
 * Nearest node `grabber` is within reach of, or `null`.
 *
 * Proximity rather than a ray: reaching out and closing your hand around something is the
 * gesture being modelled, and picking by ray would let you grab across the room by accident.
 */
export function nodeWithinReach(grabber: Grabber, state: RoomState, view: RoomView): string | null {
  let closestId: string | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const nodeId of state.nodes.keys()) {
    const mesh = view.meshFor(nodeId);

    if (!mesh || !isWithinGrabReach(grabber.position, mesh)) {
      continue;
    }

    const distance = Vector3.Distance(grabber.position, mesh.getBoundingInfo().boundingSphere.centerWorld);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestId = nodeId;
    }
  }

  return closestId;
}
